import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/en";
import { Field, useFormikContext } from "formik";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { AutocompleteText } from "../../../component/AutoComplete";
import CommonTextField from "../../../component/Input";
import CommonPhoneInput from "../../../component/PhoneInput";
import DropDownSelect from "../../../component/Select";
import { HandleSkeletion } from "../../../utils/constants";
import { CustomDiv } from "../../chat-bot/style";
import CommonFileData, {
  CoversheetUpload,
} from "../../common-components/CoverSheetFunction";
import { CommonGrid2 } from "../../common-components/StyledComponents";
import DateRangePickerComp from "../../jobs/common-card.js/DatePicker";
import { CustomizeSpan, ErrorContent } from "../../jobs/style";
import { CustomDatePicker } from "./style";

const MixedComponent = ({ editData, loading, marginTop, checkIndex }) => {
  const [fileName, setFileName] = useState("");
  const lg = useMediaQuery("(min-width:767px)");
  const initialized = useRef(false);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFileName(file.name);
    }
  };

  const { values, setFieldValue } = useFormikContext();
  const dispatch = useDispatch();
  const [localValue, setLocalValue] = React.useState({
    first_name: values.first_name || "",
    middle_name: values.middle_name || "",
    last_name: values.last_name || "",
    title: values.title || "",
    location: values.location || "",
    job_location: values.job_location || "",
    email: values.email || "",
    phone_number: values.phone_number || "",
    department: values.department || "",
    manager_name: values.manager_name || "",
    emplopyer_name: values.emplopyer_name || "",
    emplopyer_phone: values.emplopyer_phone || "",
    emplopyer_email: values.emplopyer_email || "",
    emplopyer_contact_name: values.emplopyer_contact_name || "",
    H1B_holder_company_name: values.H1B_holder_company_name || "",
    min: values["rate"]?.["min"] || "",
    max: values["rate"]?.["max"] || "",
    linkedin_url: values.linkedin_url || "",
    portfolio: values.portfolio || "",
    hour_rate: values.hour_rate || "",
    contract_hour_rate: values.contract_hour_rate || "",
    employee_hour_rate: values.employee_hour_rate || "",
    certificates_value: values.certificates_value || "",
    industry_value: values.industry_value || "",
    languages_value: values.languages_value || "",
    notice_period: values.notice_period || "",
  });

  useEffect(() => {
    if (!loading) {
      setLocalValue((prev) => ({
        ...prev,
        first_name: values.first_name || "",
        middle_name: values.middle_name || "",
        last_name: values.last_name || "",
        title: values.title || "",
        location: values.location || "",
        job_location: values.job_location || "",
        email: values.email || "",
        phone_number: values.phone_number || "",
        department: values.department || "",
        manager_name: values.manager_name || "",
        emplopyer_name: values.emplopyer_name || "",
        emplopyer_phone: values.emplopyer_phone || "",
        emplopyer_email: values.emplopyer_email || "",
        emplopyer_contact_name: values.emplopyer_contact_name || "",
        H1B_holder_company_name: values.H1B_holder_company_name || "",
        min: values["rate"]?.["min"] || "",
        max: values["rate"]?.["max"] || "",
        linkedin_url: values.linkedin_url || "",
        portfolio: values.portfolio || "",
        hour_rate: values.hour_rate || "",
        contract_hour_rate: values.contract_hour_rate || "",
        employee_hour_rate: values.employee_hour_rate || "",
        certificates_value: values.certificates_value || "",
        industry_value: values.industry_value || "",
        languages_value: values.languages_value || "",
        notice_period: values.notice_period || "",
      }));
    }
  }, [loading]);

  const debounceTimer = React.useRef(null);

  const handleInputChange = (e, item) => {
    setLocalValue((prev) => ({
      ...prev,
      [item.titleName]: e.target.value,
    }));
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setFieldValue(item.titleName, e.target.value);
    }, 100);
  };

  const handleCurrencyChange = (e, item) => {
    setLocalValue((prev) => ({
      ...prev,
      [item.currency]: e.target.value,
    }));
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      const updatedPayRate = {
        ...values[item.titleName],
        [item.currency]: e.target.value,
      };
      setFieldValue(item.titleName, updatedPayRate);
    }, 500);
  };

  const handleNumberChange = (e, countryData, item, event) => {
    const isFromDropdown =
      !event || event.type === "click" || event.target?.tagName !== "INPUT";

    let newCountry = null;
    let newNumber = e || "";

    if (isFromDropdown) {
      newCountry = countryData || null;
      newNumber = e || "";
    } else {
      const inputVal = (event?.target?.value ?? "").trim();
      const isEmpty = !inputVal || inputVal === "+";

      if (isEmpty) {
        newCountry = null;
        newNumber = "";
      } else if (inputVal.startsWith("+")) {
        newCountry = countryData || null;
        newNumber = e || "";
      } else {
        // User entered number without "+" -> do NOT treat digits as a country code
        newCountry = null;
        newNumber = inputVal.replace(/\D/g, "");
      }
    }

    // Update country immediately in Formik (null if cleared or without +)
    setFieldValue(item.country, newCountry);

    setLocalValue((prev) => ({
      ...prev,
      [item.titleName]: newNumber,
    }));

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setFieldValue(item.titleName, newNumber);
    }, 300);
  };

  const handleInputNumberChange = (e, innerItem) => {
    const inputVal = e.target.value;
    const hasFormat = !!values.phone_number_country?.format;

    let processedValue = inputVal;

    if (hasFormat) {
      const digitsOnly = inputVal.replace(/\D/g, "");
      const numberFormat = getNumberFormatWithoutDialCode(
        values.phone_number_country.format
      );
      const maxDigits = (numberFormat.match(/\./g) || []).length;
      processedValue = digitsOnly.slice(0, maxDigits);
    }

    setLocalValue((prev) => ({
      ...prev,
      [innerItem.titleName]: processedValue,
    }));

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      setFieldValue(innerItem.titleName, processedValue);
    }, 500);
  };

  const getNumberFormatWithoutDialCode = (format) => {
    return format?.replace(/^\+\.+\s*/, "") || "";
  };

  const formatPhoneNumber = (digits, format) => {
    let result = "";
    let digitIndex = 0;

    for (let i = 0; i < format.length; i++) {
      const char = format[i];
      if (char === ".") {
        if (digitIndex < digits.length) {
          result += digits[digitIndex++];
        } else {
          break;
        }
      } else {
        if (digitIndex < digits.length) {
          result += char;
        } else {
          break;
        }
      }
    }

    return result;
  };

  const getFormattedValue = (value) => {
    const fullFormat = values.phone_number_country?.format;
    const numberFormat = getNumberFormatWithoutDialCode(fullFormat);
    return formatPhoneNumber(value || "", numberFormat);
  };

  const handleRemoveFile = (index, item) => {
    const updated = values[item.titleName].filter((_, i) => i !== index);

    setFieldValue(item.titleName, updated);
  };

  const textRefs = useRef([]);
  const [showTooltipIndex, setShowTooltipIndex] = useState({});

  useEffect(() => {
    const checkOverflow = () => {
      const result = {};

      textRefs.current.forEach((el, i) => {
        if (el) {
          const isOverflow =
            el.scrollWidth > el.clientWidth ||
            el.scrollHeight > el.clientHeight;

          result[i] = isOverflow;
        }
      });

      setShowTooltipIndex(result);
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);

    return () => window.removeEventListener("resize", checkOverflow);
  }, [editData]);

  return (
    <CommonGrid2
      container
      columnSpacing={lg ? 3 : 2}
      rowSpacing={loading ? 0 : lg ? 3 : 2}
      marginTop={marginTop}
    >
      {editData?.map((item, index) => {
        return (
          <CommonGrid2
            item
            size={{
              xs: 12,
              md: item.customSize ? item.customSize : item.size ? 6 : 4,
              sm: item.customSize || 6,
            }}
            key={item.title}
            gap={"8px"}
            display={"flex"}
            customStyle={item?.customStyle}
            flexDirection={item.component === "switch" ? "row" : "column"}
            justifyContent={"flex-start"}
          >
            {loading ? (
              <HandleSkeletion height={100} />
            ) : (
              <>
                {item.component !== "split-screen" && (
                  <CommonGrid2
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    width={"100%"}
                  >
                    <Tooltip
                      arrow
                      title={showTooltipIndex[index] ? item.title : ""}
                      placement="top"
                      disableHoverListener={!showTooltipIndex[index]}
                    >
                      <Typography
                        ref={(el) => (textRefs.current[index] = el)}
                        variant={lg ? "h6" : "subtitle1"}
                        color="#344054"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                        overflow="hidden"
                      >
                        {item.checkIndex && index + 2 + ". "}
                        {item.title}
                        {item.required && (
                          <CustomizeSpan color={"red"}> *</CustomizeSpan>
                        )}
                      </Typography>
                    </Tooltip>

                    {item.type === "additional" && (
                      <Field name={item.toggleName}>
                        {({ field, form }) => {
                          const toggleValue =
                            form.values[item.toggleName] || false;
                          return (
                            <Switch
                              checked={toggleValue}
                              disabled={item.disabled}
                              onChange={(event) => {
                                form.setFieldValue(
                                  item.toggleName,
                                  event.target.checked
                                );
                              }}
                            />
                          );
                        }}
                      </Field>
                    )}
                  </CommonGrid2>
                )}
                {item.component === "input" && (
                  <Field name={item.titleName}>
                    {({ field, form }) => {
                      return (
                        <CommonTextField
                          {...field}
                          placeholder={
                            item?.placeholder ? item?.placeholder : item.title
                          }
                          disabled={item?.disabled}
                          padding="10px"
                          error={Boolean(
                            form.touched[item.titleName] &&
                              form.errors[item.titleName]
                          )}
                          placeholderFontSize={item?.placeholderFontSize}
                          helperText={
                            form.touched[item.titleName] &&
                            form.errors[item.titleName]
                          }
                          value={localValue[item.titleName]}
                          onChange={(e) => handleInputChange(e, item)}
                        />
                      );
                    }}
                  </Field>
                )}
                {item.component === "split-screen" && (
                  <CommonGrid2 container spacing={2}>
                    {item.data.map((innerItem) => (
                      <CommonGrid2
                        size={innerItem.size}
                        display={"flex"}
                        flexDirection={"column"}
                        gap={"8px"}
                      >
                        <Tooltip arrow title={innerItem.title} placement="top">
                          <Typography
                            variant={lg ? "h6" : "subtitle1"}
                            color="#344054"
                            textOverflow={"ellipsis"}
                            whiteSpace={"nowrap"}
                            overflow={"hidden"}
                          >
                            {innerItem.title}
                            {innerItem.required && (
                              <CustomizeSpan color={"red"}> *</CustomizeSpan>
                            )}
                          </Typography>
                        </Tooltip>
                        {innerItem.component === "phone-number" && (
                          <Field name={innerItem.titleName}>
                            {({ field, form }) => {
                              const isError = Boolean(
                                form.touched[innerItem.titleName] &&
                                  form.errors[innerItem.titleName]
                              );
                              const storedCountryCode =
                                values?.[innerItem?.country]?.countryCode ||
                                "";
                              const dialCode =
                                values?.[innerItem?.country]?.dialCode || "";
                              const phoneNumberValue =
                                values?.[innerItem?.titleName];
                              const defaultCountry =
                                storedCountryCode ||
                                (!phoneNumberValue ? "us" : "");
                              const shouldDisableInitialGuess =
                                !storedCountryCode && Boolean(phoneNumberValue);

                              let rawValue =
                                localValue[innerItem.titleName] ?? "";
                              return (
                                <>
                                  <CommonPhoneInput
                                    placeholder={innerItem?.placeholder}
                                    padding={
                                      lg
                                        ? "14px 0px 14px 60px"
                                        : "12.5px 0px 12.5px 60px"
                                    }
                                    country={defaultCountry}
                                    value={rawValue}
                                    disabled={innerItem.disabled}
                                    disableCountryGuess={false}
                                    disableInitialCountryGuess={
                                      shouldDisableInitialGuess
                                    }
                                    customStyle={{
                                      "& .selected-flag": {
                                        cursor: !innerItem.disabled
                                          ? "pointer"
                                          : "auto",
                                        borderRadius: "4px 0 0 4px",
                                      },
                                      "& .form-control ": {
                                        backgroundColor: innerItem.disabled
                                          ? "#f5f5f5 !important"
                                          : "transparent !important",
                                      },
                                      "& .flag-dropdown": {
                                        backgroundColor: "#fff !important",
                                      },
                                    }}
                                    onChange={(e, countryData, event) => {
                                      handleNumberChange(
                                        e,
                                        countryData,
                                        innerItem,
                                        event
                                      );
                                    }}
                                  />
                                  {isError && (
                                    <ErrorContent
                                      margin="-2px 0px 0px"
                                      className="Mui-error"
                                    >
                                      {form.errors[innerItem.titleName]}
                                    </ErrorContent>
                                  )}
                                </>
                              );
                            }}
                          </Field>
                        )}
                        {innerItem.component === "input" && (
                          <Field name={innerItem.titleName}>
                            {({ field, form }) => {
                              return (
                                <CommonTextField
                                  {...field}
                                  placeholder={
                                    innerItem?.placeholder
                                      ? innerItem?.placeholder
                                      : innerItem.title
                                  }
                                  disabled={innerItem?.disabled}
                                  padding="12px"
                                  error={Boolean(
                                    form.touched[innerItem.titleName] &&
                                      form.errors[innerItem.titleName]
                                  )}
                                  placeholderFontSize={
                                    innerItem?.placeholderFontSize
                                  }
                                  helperText={
                                    form.touched[innerItem.titleName] &&
                                    form.errors[innerItem.titleName]
                                  }
                                  maxLength={5}
                                  value={localValue[innerItem.titleName]}
                                  onChange={(e) =>
                                    handleInputChange(e, innerItem)
                                  }
                                />
                              );
                            }}
                          </Field>
                        )}

                        {innerItem.component === "country-data" && (
                          <Field name={innerItem.titleName}>
                            {({ field, form }) => {
                              const isError = Boolean(
                                form.touched[innerItem.titleName] &&
                                  form.errors[innerItem.titleName]
                              );
                              const storedCountryCode =
                                values?.[innerItem?.titleName]?.countryCode ||
                                "";
                              const phoneNumberField = item.data.find(
                                (d) => d.component === "input-number"
                              )?.titleName;
                              const phoneNumberValue = phoneNumberField
                                ? values?.[phoneNumberField]
                                : "";
                              const defaultCountry =
                                storedCountryCode ||
                                (!phoneNumberValue ? "us" : "");
                              return (
                                <>
                                  <CommonPhoneInput
                                    country={defaultCountry}
                                    placeholder={innerItem?.placeholder}
                                    // value={
                                    //   ""
                                    // }
                                    onChange={(e, countryData) => {
                                      console.log(countryData, "countryData");
                                      setFieldValue(
                                        innerItem.country,
                                        countryData
                                      );
                                    }}
                                    padding={
                                      lg
                                        ? "14px 0px 14px 60px"
                                        : "12.5px 0px 12.5px 60px"
                                    }
                                    customStyle={{
                                      "& .selected-flag": {
                                        cursor: !innerItem.disabled
                                          ? "pointer"
                                          : "auto",
                                        borderRadius: "4px 0 0 4px",
                                      },
                                      "& .form-control ": {
                                        backgroundColor: innerItem.disabled
                                          ? "#f5f5f5 !important"
                                          : "transparent !important",
                                      },
                                      "& .flag-dropdown": {
                                        backgroundColor: "#fff !important",
                                      },
                                    }}
                                    disabled={innerItem.disabled}
                                    textDisabled={true}
                                  />

                                  {isError && (
                                    <ErrorContent
                                      margin="-2px 0px 0px"
                                      className="Mui-error"
                                    >
                                      {form.errors[innerItem.titleName]}
                                    </ErrorContent>
                                  )}
                                </>
                              );
                            }}
                          </Field>
                        )}
                        {innerItem.component === "input-number" && (
                          <Field name={innerItem.titleName}>
                            {({ field, form }) => {
                              return (
                                <CommonTextField
                                  {...field}
                                  placeholder={
                                    innerItem?.placeholder
                                      ? innerItem?.placeholder
                                      : innerItem.title
                                  }
                                  disabled={innerItem?.disabled}
                                  padding="12px"
                                  error={Boolean(
                                    form.touched[innerItem.titleName] &&
                                      form.errors[innerItem.titleName]
                                  )}
                                  placeholderFontSize={
                                    innerItem?.placeholderFontSize
                                  }
                                  helperText={
                                    form.touched[innerItem.titleName] &&
                                    form.errors[innerItem.titleName]
                                  }
                                  value={
                                    values.phone_number_country
                                      ? getFormattedValue(
                                          localValue[innerItem.titleName]
                                        )
                                      : localValue[innerItem.titleName]
                                  }
                                  // value={localValue[innerItem.titleName]}
                                  onChange={(e) =>
                                    handleInputNumberChange(e, innerItem)
                                  }
                                />
                              );
                            }}
                          </Field>
                        )}
                      </CommonGrid2>
                    ))}
                  </CommonGrid2>
                )}
                {item.component === "dropDown" && (
                  <Field name={item.titleName}>
                    {({ field, form }) => {
                      const handleDropdownChange = (event) => {
                        form.setFieldValue(item.titleName, event.target.value);
                      };

                      return (
                        <DropDownSelect
                          marginTop="0px"
                          title={
                            item?.placeholder ? item?.placeholder : item.title
                          }
                          disabled={item?.disabled}
                          removeAnimation={true}
                          data={form.values[item.dropDownData]}
                          error={Boolean(
                            form.touched[item.titleName] &&
                              form.errors[item.titleName]
                          )}
                          helperText={
                            form.touched[item.titleName] &&
                            form.errors[item.titleName]
                          }
                          value={form.values[item.titleName]}
                          handleChange={handleDropdownChange}
                        />
                      );
                    }}
                  </Field>
                )}
                {item.component === "calender" && (
                  <Field name={item.titleName}>
                    {({ field, form }) => {
                      const isError =
                        form.touched[item.titleName] &&
                        Boolean(form.errors[item.titleName]);

                      const helperText = isError
                        ? form.errors[item.titleName]
                        : "";

                      return (
                        <>
                          <DateRangePickerComp
                            minDate={item.minDate}
                            maxDate={item.maxDate}
                            startDateProp={
                              form.values?.[item.titleName]?.start_date
                            }
                            endDateProp={
                              form.values?.[item.titleName]?.end_date
                            }
                            handleChange={(start_date, end_date) => {
                              form.setFieldValue(item.titleName, {
                                start_date: start_date,
                                end_date: end_date,
                              });
                            }}
                            disabled={item.disabled}
                            error={isError}
                            customStyle={{
                              "& .rdrDefinedRangesWrapper": {
                                display: "none",
                              },
                            }}
                          />
                          {isError && <ErrorContent>{helperText}</ErrorContent>}
                        </>
                      );
                    }}
                  </Field>
                )}

                {item.component === "autoComplete" && (
                  <Field name={item.titleName}>
                    {({ field, form }) => {
                      const handleAutoCompleteChange = (e, newValue) => {
                        const checkData = form?.values[item.titleName];
                        if (e?.key === "Backspace" && e.target.value === "") {
                          const updatedValue = [
                            ...(form.values[item.titleName] || []),
                          ];
                          updatedValue.pop();
                          form.setFieldValue(item.titleName, updatedValue);
                        } else {
                          const formattedValues = newValue.map(
                            (item, index) => {
                              if (typeof item === "string") {
                                return {
                                  content: item,
                                  ...(checkData[0]?.name
                                    ? { name: "inferred" }
                                    : { id: index }),
                                };
                              }
                              return item;
                            }
                          );

                          form.setFieldValue(item.titleName, formattedValues);
                        }
                      };

                      const handleDelete = (option) => {
                        const updatedValue = form.values[item.titleName].filter(
                          (item, i) => item !== option
                        );
                        form.setFieldValue(item.titleName, updatedValue);
                      };

                      
                      const handleKeyDown = (e) => {
                        const checkData = form?.values[item.titleName];
                        const dataValue = e.target.value;
                        if (e.key === "Tab" ) {                         
                          e.preventDefault();
                          e.stopPropagation();
                          setLocalValue((prev) => ({
                            ...prev,
                            [item.inputValue]: "",
                          }));

                          const data = {
                            content: dataValue,
                            ...(checkData[0]?.name
                              ? { name: "inferred" }
                              : { id: checkData.length }),
                          };

                          const updatedValue = [...checkData, data];

                          form.setFieldValue(
                            item.titleName,
                            updatedValue,
                            false
                          );

                          form.setFieldValue(item.inputValue, "", false);

                          // Clear error if exists
                          setTimeout(() => {
                            if (updatedValue.length > 0) {
                              form.setFieldError(item.titleName, "");
                            }
                          }, 0);
                        }
                        else if (e.key === "Enter") {
                          e.preventDefault();
                          e.stopPropagation();
                        }
                      };

                      const handleBlur = (e) => {
                        const checkData = form?.values[item.titleName] || [];
                        const typedValue = (localValue[item.inputValue] ?? e.target?.value ?? "").trim();
                        setLocalValue((prev) => ({
                          ...prev,
                          [item.inputValue]: "",
                        }));
                        if (typedValue !== "") {
                          const data = {
                            content: typedValue,
                            ...(checkData[0]?.name
                              ? { name: "inferred" }
                              : { id: checkData.length }),
                          };
                          const updatedValue = [...checkData, data];
                          form.setFieldValue(
                            item.titleName,
                            updatedValue,
                            false
                          );
                          form.setFieldValue(item.inputValue, "", false);
                          setTimeout(() => {
                            if (updatedValue.length > 0) {
                              form.setFieldError(item.titleName, "");
                            }
                          }, 0);
                        }
                      };

                      const isError =
                        form.touched[item.titleName] &&
                        form.errors[item.titleName];
                      const helperText = isError
                        ? form.errors[item.titleName]
                        : "";
                      return (
                        <>
                          <AutocompleteText
                            autoData={{
                              options: [],
                              key: `autocomplete-${item.titleName}`,
                              title: "Select Emails",
                              placeholder: item.placeholder,
                              isDisabled: item.disabled,
                              value: form.values[item.titleName] || "",
                              onDelete: handleDelete,
                              onKeyDown: handleKeyDown,
                              onChange: handleAutoCompleteChange,
                              onBlur: handleBlur,
                              inputValue: localValue[item.inputValue],
                              onInputChange: (e, newValue) => {
                                setLocalValue((prev) => ({
                                  ...prev,
                                  [item.inputValue]: newValue,
                                }));
                              },
                              limitTags: item.disabled
                                ? form.values[item.titleName]?.length
                                : true,
                            }}
                            error={isError}
                          />
                          {isError && (
                            <ErrorContent margin={"-5px 0px 0px"}>
                              {helperText}
                            </ErrorContent>
                          )}
                        </>
                      );
                    }}
                  </Field>
                )}

                {item.component === "fileUpload" && (
                  <CommonTextField
                    readOnly={true}
                    handleFileChange={handleFileChange}
                    value={fileName}
                    padding="10px"
                  />
                )}

                {item.component === "input-link" && (
                  <Field name={item.titleName}>
                    {({ field, form }) => {
                      return (
                        <CommonTextField
                          {...field}
                          disabled={item.disabled}
                          placeholder={item.placeholder}
                          padding="10px"
                          startIcon={"http://"}
                          error={Boolean(
                            form.touched[item.titleName] &&
                              form.errors[item.titleName]
                          )}
                          placeholderFontSize={item?.placeholderFontSize}
                          helperText={
                            form.touched[item.titleName] &&
                            form.errors[item.titleName]
                          }
                          value={localValue[item.titleName]}
                          onChange={(e) => handleInputChange(e, item)}
                          customStyle={{
                            "& .MuiInputAdornment-root": {
                              maxHeight: "none",
                              height: "100%",
                              borderRight: "1px solid #D0D5DD",
                              paddingRight: "10px",
                            },
                          }}
                        />
                      );
                    }}
                  </Field>
                )}
                {item.component === "input-link-linkedin" && (
                  <Field name={item.titleName}>
                    {({ field, form }) => {
                      return (
                        <CommonTextField
                          {...field}
                          placeholder={item.placeholder}
                          padding="10px"
                          // endIcon={form.values["first_name"]}
                          error={Boolean(
                            form.touched[item.titleName] &&
                              form.errors[item.titleName]
                          )}
                          placeholderFontSize={item?.placeholderFontSize}
                          helperText={
                            form.touched[item.titleName] &&
                            form.errors[item.titleName]
                          }
                          value={localValue[item.titleName]}
                          onChange={(e) => handleInputChange(e, item)}
                          disabled={item.disabled}
                          customStyle={{
                            "& .MuiInputAdornment-root": {
                              maxHeight: "none",
                              height: "100%",
                              borderLeft: "1px solid #D0D5DD",
                              paddingLeft: "1rem",
                            },
                          }}
                        />
                      );
                    }}
                  </Field>
                )}
                {item.component === "single-calender" && (
                  <Field name={item.titleName}>
                    {({ field, form }) => {
                      const isError =
                        form.touched[item.titleName] &&
                        Boolean(form.errors[item.titleName]);

                      const helperText = isError
                        ? form?.errors[item.titleName]
                        : "";

                      return (
                        <>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <CustomDatePicker
                              desktopModeMediaQuery="(min-width:0px)"
                              disabled={item.disabled}
                              onChange={(newValue) => {
                                const formattedDate = newValue
                                  ? dayjs(newValue).format("DD MMM YYYY")
                                  : "";

                                form.setFieldValue(
                                  item?.titleName,
                                  formattedDate
                                );
                              }}
                              value={
                                form?.values?.[item?.titleName]
                                  ? dayjs(
                                      form.values[item.titleName],
                                      "DD MMM YYYY"
                                    )
                                  : null
                              }
                              slots={{
                                openPickerIcon: CalendarTodayIcon,
                              }}
                              format="DD MMM YYYY"
                              views={["year", "month", "day"]}
                              slotProps={{
                                textField: {
                                  inputProps: { readOnly: false }, // ✅ only readonly
                                  sx: {
                                    pointerEvents: "none",
                                    "& .MuiInputAdornment-root": {
                                      pointerEvents: "auto",
                                    },
                                    "& .MuiInputBase-input": {
                                      backgroundColor: "#F2F4F7",
                                      color: "#666",
                                      cursor: "pointer",
                                    },
                                  },
                                },
                              }}
                            />
                            {/* <CustomDatePicker
                              disabled={item.disabled}
                              onChange={(newValue) => {
                                const formattedDate = newValue
                                  ? dayjs(newValue).format("DD MMM YYYY")
                                  : "";

                                form.setFieldValue(
                                  item?.titleName,
                                  formattedDate
                                );
                              }}
                              value={
                                form?.values?.[item?.titleName]
                                  ? dayjs(
                                      form.values[item.titleName],
                                      "DD MMM YYYY"
                                    )
                                  : null
                              }
                              format={"DD MMM YYYY"}
                              error={isError}
                              views={["year", "month", "day"]}
                              slotProps={{
                                textField: {
                                  disabled: true,
                                  inputProps: { readOnly: true },
                                  sx: {
                                    "& .MuiInputBase-input": {
                                      backgroundColor: "#F2F4F7",
                                      color: "#666",
                                      cursor: "not-allowed",
                                    },
                                  },
                                },
                              }}
                              slots={{
                                openPickerIcon: () => (
                                  <CommonGrid2
                                    display={"flex"}
                                    fontSize={"15px"}
                                    gap={"5px"}
                                    alignItems={"center"}
                                    color={"#000"}
                                    fontWeight={600}
                                  >
                                    <CalendarTodayIcon
                                      style={{ height: "20px" }}
                                    />
                                    <Typography
                                      fontWeight={600}
                                      fontSize={"13px"}
                                    >
                                      Select
                                    </Typography>
                                  </CommonGrid2>
                                ),
                              }}
                            /> */}
                          </LocalizationProvider>
                          {isError && (
                            <ErrorContent
                              margin={"-5px 0px 0px"}
                              className="Mui-error"
                            >
                              {helperText}
                            </ErrorContent>
                          )}
                        </>
                      );
                    }}
                  </Field>
                )}
                {item.component === "dob_month" && (
                  <Field name={item.titleName}>
                    {({ field, form }) => {
                      const isError =
                        form.touched[item.titleName] &&
                        Boolean(form.errors[item.titleName]);

                      const helperText = isError
                        ? form?.errors[item.titleName]
                        : "";

                      return (
                        <>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <CustomDatePicker
                              disabled={item.disabled}
                              sizeMedium="-4px"
                              label="Pick date"
                              desktopModeMediaQuery="(min-width:0px)"
                              slots={{
                                calendarHeader: (props) => (
                                  <CustomDiv
                                    padding={"1rem 2rem"}
                                    fontSize="16px"
                                    fontWeight={600}
                                  >
                                    {dayjs(props.currentMonth).format("MMMM")}
                                  </CustomDiv>
                                ),
                                openPickerIcon: CalendarTodayIcon,
                              }}
                              value={
                                form?.values?.[item?.titleName]
                                  ? dayjs(form.values[item.titleName], "DD MMM")
                                  : null
                              }
                              onChange={(newValue) => {
                                const formattedDate = newValue
                                  ? dayjs(newValue).format("DD MMM")
                                  : "";

                                form.setFieldValue(
                                  item?.titleName,
                                  formattedDate
                                );
                              }}
                              openTo="month"
                              format="DD MMM"
                              views={["month", "day"]}
                              slotProps={{
                                textField: {
                                  inputProps: { readOnly: true },
                                  sx: {
                                    pointerEvents: "none",
                                    "& .MuiInputAdornment-root": {
                                      pointerEvents: "auto",
                                    },
                                    "& .MuiInputBase-input": {
                                      backgroundColor: "#F2F4F7",
                                      color: "#666",
                                      cursor: "pointer",
                                    },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider>
                          {/* <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <CustomDatePicker
                              disabled={item.disabled}
                              onChange={(newValue) => {
                                const formattedDate = newValue
                                  ? dayjs(newValue).format("DD MMM")
                                  : "";

                                form.setFieldValue(
                                  item?.titleName,
                                  formattedDate
                                );
                              }}
                              value={
                                form?.values?.[item?.titleName]
                                  ? dayjs(form.values[item.titleName], "DD MMM")
                                  : null
                              }
                              openTo="month"
                              format={"DD MMM"}
                              error={isError}
                              views={["month", "day"]}
                              slots={{
                                calendarHeader: (props) => (
                                  <CustomDiv
                                    padding={"1rem 2rem"}
                                    fontSize="16px"
                                    fontWeight={600}
                                  >
                                    {dayjs(props.currentMonth).format("MMMM")}
                                  </CustomDiv>
                                ),
                                openPickerIcon: () => (
                                  <CommonGrid2
                                    display={"flex"}
                                    fontSize={"15px"}
                                    gap={"5px"}
                                    alignItems={"center"}
                                    color={"#000"}
                                    fontWeight={600}
                                  >
                                    <CalendarTodayIcon
                                      style={{ height: "20px" }}
                                    />
                                  </CommonGrid2>
                                ),
                              }}
                              slotProps={{
                                textField: {
                                  placeholder: "DD MMM",
                                  disabled: true,
                                  inputProps: { readOnly: true },
                                  sx: {
                                    "& .MuiInputBase-input": {
                                      backgroundColor: item.disabled
                                        ? "#F2F4F7"
                                        : "transparent",
                                      color: item.disabled ? "#666" : "#667085",
                                      cursor: "not-allowed",
                                    },
                                  },
                                },
                                popper: {
                                  sx: {
                                    "& .MuiDateCalendar-root": {
                                      height: "auto",
                                      marginBottom: "1rem",
                                    },
                                  },
                                },
                              }}
                            />
                          </LocalizationProvider> */}
                          {isError && (
                            <ErrorContent
                              margin={"-5px 0px 0px"}
                              className="Mui-error"
                            >
                              {helperText}
                            </ErrorContent>
                          )}
                        </>
                      );
                    }}
                  </Field>
                )}
                {item.component === "hour" && (
                  <Field name={item.titleName}>
                    {({ field, form }) => {
                      return (
                        <CommonTextField
                          placeholder={item.placeholder}
                          onChange={(e) => handleInputChange(e, item)}
                          padding="10px"
                          disabled={item.disabled}
                          error={
                            form.touched[item.titleName] &&
                            form.errors[item.titleName]
                          }
                          placeholderFontSize={item?.placeholderFontSize}
                          helperText={
                            form.touched[item.titleName] &&
                            form.errors[item.titleName]
                          }
                          startIcon={"$"}
                          value={localValue[item.titleName]}
                        />
                      );
                    }}
                  </Field>
                )}

                {item.component === "input-select" && (
                  <Field name={item.titleName}>
                    {({ field, form }) => {
                      return (
                        <CommonTextField
                          startShowSelect={true}
                          menuItemData={item.menuItemData}
                          currency={values[item.currencyValue]}
                          handleCurrencyChange={(e) => {
                            console.log(e.target.value, "sflksjflsklskf");
                            setFieldValue(item.currencyValue, e.target.value);
                          }}
                          placeholder={item.placeholder}
                          onChange={(e) => handleInputChange(e, item)}
                          padding="10px"
                          disabled={item.disabled}
                          error={
                            form.touched[item.titleName] &&
                            form.errors[item.titleName]
                          }
                          placeholderFontSize={item?.placeholderFontSize}
                          helperText={
                            form.touched[item.titleName] &&
                            form.errors[item.titleName]
                          }
                          value={localValue[item.titleName]}
                          customStyle={{
                            "& .MuiInputAdornment-positionStart": {
                              marginRight: "10px",
                            },
                            "& .MuiSelect-root": {
                              borderRadius: "0px",
                              right: "10px",
                              borderRight: "1px solid #D0D5DD",
                            },
                          }}
                        />
                      );
                    }}
                  </Field>
                )}

                {item.component === "toggle" && (
                  <Field name={item.titleName}>
                    {({ field, form }) => {
                      return (
                        <FormControl
                          error={Boolean(
                            form.touched[item.titleName] &&
                              form.errors[item.titleName]
                          )}
                        >
                          <RadioGroup
                            row
                            value={values[item.titleName]}
                            onChange={(e) => {
                              const newValue = e.target.value;

                              if (item.titleName === "citizen_status") {
                                if (newValue === "Citizen") {
                                  setFieldValue("visa_type", newValue);
                                } else {
                                  setFieldValue("visa_type", "");
                                }
                              }

                              setFieldValue(item.titleName, newValue);
                            }}
                            sx={{
                              margin: "0px 0px 0px 0.9rem",
                            }}
                          >
                            {item.toggleData.map((option) => (
                              <FormControlLabel
                                key={option}
                                value={option}
                                disabled={item.disabled}
                                control={<Radio />}
                                label={option}
                                sx={{
                                  "& .MuiRadio-root": {
                                    padding: "7px",
                                  },
                                  "& .MuiTypography-root": {
                                    fontSize: "14px",
                                  },
                                }}
                              />
                            ))}
                          </RadioGroup>

                          {form.touched[item.titleName] &&
                            form.errors[item.titleName] && (
                              <ErrorContent className="Mui-error">
                                {form.errors[item.titleName]}
                              </ErrorContent>
                            )}
                        </FormControl>
                      );
                    }}
                  </Field>
                )}

                {item.component === "upload" && (
                  <Fragment>
                    <CommonGrid2 spacing={2} container>
                      <CommonFileData
                        handleRemoveFile={handleRemoveFile}
                        item={{
                          titleName: item.titleName,
                          uploadCustomSize: item.uploadCustomSize,
                        }}
                        values={values[item.titleName]}
                      />
                    </CommonGrid2>

                    <CoversheetUpload
                      values={values}
                      setFieldValue={setFieldValue}
                      item={{
                        titleName: item.titleName,
                        file_name: item.file_name,
                      }}
                    />

                    {/* <label
                      htmlFor={`contained-${item.titleName}`}
                      style={{
                        width: "fit-content",
                        margin:
                          values[item.titleName].length === 0
                            ? "0px"
                            : "10px 0px 0px",
                      }}
                    >
                      <Input
                        // accept="*"
                        id={`contained-${item.titleName}`}
                        multiple
                        accept=".pdf,.doc,.docx"
                        type="file"
                        sx={{ display: "none" }}
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (!file) return;
                          const allowedTypes = [
                            "application/pdf",
                            "application/msword",
                            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                          ];
                          if (!allowedTypes.includes(file.type)) {
                            dispatch(
                              ShowSnackbar({
                                open: true,
                                message:
                                  "Only PDF, DOC, DOCX files are allowed",
                                severity: "error",
                              })
                            );
                            e.target.value = null;
                            return;
                          }
                          const previousFiles = values[item.titleName] || [];
                          const isDuplicate = previousFiles.some(
                            (f) => f.name === file.name && f.size === file.size
                          );
                          if (isDuplicate) {
                            dispatch(
                              ShowSnackbar({
                                open: true,
                                message: "This file is already uploaded",
                                severity: "error",
                              })
                            );

                            e.target.value = null;
                            return;
                          }
                          if (item.single_upload) {
                            setFieldValue(item.titleName, [file]);
                          } else {
                            setFieldValue(item.titleName, [
                              ...previousFiles,
                              file,
                            ]);
                          }
                          e.target.value = null;
                        }}
                      />
                      <CommonButton
                        value={"Upload"}
                        variant="outlined"
                        startIcon={<UploadIco />}
                        component="span"
                        gap={"0px"}
                        color="#344054"
                        svg={true}
                      />
                    </label> */}
                  </Fragment>
                )}

                {/* {item.component === "phone-number" && (
                  <Field name={item.titleName}>
                    {({ field, form }) => {
                      const isError = Boolean(
                        form.touched[item.titleName] &&
                          form.errors[item.titleName]
                      );
                      return (
                        <>
                          <CommonPhoneInput
                            placeholder={item?.placeholder}
                            country=""
                            value={localValue[item.titleName]}
                            onChange={(e, countryData) => {
                              console.log(countryData, "sfsfksnfjksfjknsfnjk");
                              handleNumberChange(e, countryData, item);
                            }}
                            customStyle={{
                              "& input": {
                                display: "none",
                              },
                              "& .flag-dropdown": {
                                padding: "18px 10px 18px 1px !important",
                              },
                            }}
                          />

                          {isError && (
                            <ErrorContent
                              margin="-2px 0px 0px"
                              className="Mui-error"
                            >
                              {form.errors[item.titleName]}
                            </ErrorContent>
                          )}
                        </>
                      );
                    }}
                  </Field>
                )} */}
              </>
            )}
          </CommonGrid2>
        );
      })}
    </CommonGrid2>
  );
};
export default MixedComponent;
